import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Wallet,
  DollarSign,
  AlertTriangle,
  BarChart2,
  Scale,
  FileText,
  Check,
  X,
} from "lucide-react";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import Button from "../../components/Button";
import PageLoader from "../../components/PageLoader";
import {
  getBrokerByNameRaw as apiGetBrokerByNameRaw,
  getBrokerBySlugRaw as apiGetBrokerBySlugRaw,
} from "../../api";

const DASH = "—";

const toTextOrDash = (v) => {
  if (v == null) return DASH;
  if (typeof v === "string") {
    const s = v.trim();
    return s ? s : DASH;
  }
  if (typeof v === "number") return Number.isFinite(v) ? String(v) : DASH;
  return DASH;
};

const toNumberOrNull = (v) => {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

const percentToWidth = (v) => {
  const s = typeof v === "string" ? v.trim() : "";
  if (!s || s === DASH) return "0%";
  return s.endsWith("%") ? s : "0%";
};

const BrokerDetail = () => {
  const { brokerName } = useParams();
  const navigate = useNavigate();
  const decodedName = decodeURIComponent(brokerName || "");
  const [broker, setBroker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState({});
  const [metrics, setMetrics] = useState({
    rating: null,
    activeClients: DASH,
    activeClientsTrend: DASH,
    charges: { delivery: DASH, intraday: DASH, futures: DASH },
    complaints: { total: null, resolved: null },
    shareHolding: { promoters: DASH, public: DASH },
    pros: [],
    cons: [],
    financials: { revenue: DASH, profit: DASH, marketCap: DASH },
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // Prefer slug-based endpoint for clean URLs; fallback to by-name for older links
        let bRaw;
        try {
          bRaw = await apiGetBrokerBySlugRaw(decodedName);
        } catch (_e) {
          // If the param looks like a slug, try best-effort convert to name
          const maybeName = decodedName.includes("-")
            ? decodedName
                .split("-")
                .map((s) => (s ? s[0].toUpperCase() + s.slice(1) : ""))
                .join(" ")
            : decodedName;
          bRaw = await apiGetBrokerByNameRaw(maybeName);
        }
        if (!mounted) return;
        setBroker({
          id: bRaw?._id?.toString?.() ?? bRaw?.id,
          _id: bRaw?._id,
          name: bRaw?.name || decodedName,
          slug: bRaw?.slug,
          logo: bRaw?.logo || "📊",
          color: bRaw?.color || "from-primary-400 to-primary-600",
        });
        setDetails({
          rating: toNumberOrNull(bRaw?.rating),
          activeClients: toTextOrDash(bRaw?.activeClients),
          brokerage: toTextOrDash(bRaw?.brokerage),
          features: Array.isArray(bRaw?.features)
            ? bRaw.features.filter(Boolean)
            : [],
          description: toTextOrDash(bRaw?.description),
        });
        setMetrics({
          rating: toNumberOrNull(bRaw?.rating),
          activeClients: toTextOrDash(bRaw?.activeClients),
          activeClientsTrend: toTextOrDash(bRaw?.activeClientsTrend),
          charges: {
            delivery: toTextOrDash(bRaw?.charges?.delivery),
            intraday: toTextOrDash(bRaw?.charges?.intraday),
            futures: toTextOrDash(bRaw?.charges?.futures),
          },
          complaints: {
            total: toNumberOrNull(bRaw?.complaints?.total),
            resolved: toNumberOrNull(bRaw?.complaints?.resolved),
          },
          shareHolding: {
            promoters: toTextOrDash(bRaw?.shareHolding?.promoters),
            public: toTextOrDash(bRaw?.shareHolding?.public),
          },
          pros: Array.isArray(bRaw?.pros) ? bRaw.pros.filter(Boolean) : [],
          cons: Array.isArray(bRaw?.cons) ? bRaw.cons.filter(Boolean) : [],
          financials: {
            revenue: toTextOrDash(bRaw?.financials?.revenue),
            profit: toTextOrDash(bRaw?.financials?.profit),
            marketCap: toTextOrDash(bRaw?.financials?.marketCap),
          },
        });
      } catch (err) {
        if (!mounted) return;
        setBroker(null);
        setDetails({});
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [decodedName]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <main className="flex-1 relative">
          <PageLoader />
        </main>
        {/* <Footer /> */}
      </div>
    );
  }

  if (!broker) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center px-4">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Broker not found
            </h1>
            <Button variant="primary" onClick={() => navigate("/brokers")}>
              Back to All Brokers
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <main className="flex-1">
        <div className="max-w-4xl 4xl:max-w-10xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Back + Title */}
          <div className="mb-5">
            <button
              onClick={() => navigate(-1)}
              className="text-primary-600 hover:text-primary-700 font-semibold mb-4 flex items-center gap-2"
            >
              ← Back
            </button>
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`w-16 h-16 bg-gradient-to-br ${broker.color} rounded-xl flex items-center justify-center text-white text-3xl shadow-lg`}
              >
                {broker.logo}
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                  {broker.name}
                </h1>
                <p className="text-gray-600 mt-1">{details.description}</p>
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        className={`w-5 h-5 ${metrics.rating != null && i < Math.floor(metrics.rating) ? "text-yellow-400" : "text-gray-300"}`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <span className="text-gray-600 font-semibold">
                    {metrics.rating != null ? `${metrics.rating}/5.0` : DASH}
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              {/* <Button variant="primary" onClick={() => navigate(`/compare?broker1=${encodeURIComponent(broker.name)}&broker2=Zerodha`)}>
                Compare with another broker
              </Button> */}
              {/* <Button variant="secondary" onClick={() => navigate('/brokers')}>
                View all brokers
              </Button> */}
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-6 3xl:space-y-0 3xl:grid 3xl:grid-cols-2 3xl:gap-8 4xl:grid-cols-3">
            {/* Active Clients */}
            <section className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600">
                  <Wallet className="w-5 h-5" />
                </span>
                Active Clients
              </h2>
              <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-4 border border-primary-200">
                <div className="text-sm text-gray-600 mb-1">Active Clients</div>
                <div className="text-2xl font-bold text-primary-700">
                  {metrics.activeClients}
                </div>
                <div className="text-xs text-green-600 font-medium mt-1">
                  ↑ {metrics.activeClientsTrend}
                </div>
              </div>
            </section>

            {/* Charges */}
            <section className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600">
                  <DollarSign className="w-5 h-5" />
                </span>
                Charges
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-700">Delivery</span>
                  <span className="font-semibold text-gray-900">
                    {metrics.charges.delivery}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-700">Intraday</span>
                  <span className="font-semibold text-gray-900">
                    {metrics.charges.intraday}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-700">Futures</span>
                  <span className="font-semibold text-gray-900">
                    {metrics.charges.futures}
                  </span>
                </div>
              </div>
            </section>

            {/* Complaints */}
            <section className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600">
                  <AlertTriangle className="w-5 h-5" />
                </span>
                Complaints
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-700">
                    Total Complaints
                  </span>
                  <span className="font-bold text-gray-900">
                    {metrics.complaints.total != null
                      ? metrics.complaints.total
                      : DASH}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-700">Resolved</span>
                  <span className="font-bold text-green-600">
                    {metrics.complaints.resolved != null
                      ? metrics.complaints.resolved
                      : DASH}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-4">
                  <div
                    className="bg-green-500 h-2 rounded-full"
                    style={{
                      width: `${
                        metrics.complaints.total &&
                        metrics.complaints.resolved != null
                          ? (metrics.complaints.resolved /
                              metrics.complaints.total) *
                            100
                          : 0
                      }%`,
                    }}
                  />
                </div>
              </div>
            </section>

            {/* Share Holding */}
            {/* <section className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600">
                  <BarChart2 className="w-5 h-5" />
                </span>
                Share Holding
              </h2>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-gray-700">Promoters</span>
                    <span className="font-bold text-gray-900">
                      {metrics.shareHolding.promoters}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-primary-500 h-3 rounded-full"
                      style={{
                        width: percentToWidth(metrics.shareHolding.promoters),
                      }}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-gray-700">Public</span>
                    <span className="font-bold text-gray-900">
                      {metrics.shareHolding.public}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-secondary-500 h-3 rounded-full"
                      style={{
                        width: percentToWidth(metrics.shareHolding.public),
                      }}
                    />
                  </div>
                </div>
              </div>
            </section> */}

            {/* Pros & Cons */}
            <section className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600">
                  <Scale className="w-5 h-5" />
                </span>
                Pros & Cons
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h5 className="font-semibold text-green-600 mb-2">Pros</h5>
                  <ul className="space-y-2 text-sm text-gray-700">
                    {metrics.pros.length ? (
                      metrics.pros.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-green-700">{item}</span>
                        </li>
                      ))
                    ) : (
                      <li className="text-gray-500">{DASH}</li>
                    )}
                  </ul>
                </div>
                <div>
                  <h5 className="font-semibold text-red-600 mb-2">Cons</h5>
                  <ul className="space-y-2 text-sm text-gray-700">
                    {metrics.cons.length ? (
                      metrics.cons.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <X className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                          <span className="text-red-700">{item}</span>
                        </li>
                      ))
                    ) : (
                      <li className="text-gray-500">{DASH}</li>
                    )}
                  </ul>
                </div>
              </div>
            </section>

            {/* Financials */}
            <section className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600">
                  <FileText className="w-5 h-5" />
                </span>
                Financials
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-700">Revenue</span>
                  <span className="font-semibold text-gray-900">
                    {metrics.financials.revenue}
                  </span>
                </div>
                <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-700">Profit</span>
                  <span className="font-semibold text-gray-900">
                    {metrics.financials.profit}
                  </span>
                </div>
                <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-700">Market Cap</span>
                  <span className="font-semibold text-gray-900">
                    {metrics.financials.marketCap}
                  </span>
                </div>
              </div>
            </section>

            {/* Features */}
            <section className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Features</h2>
              {Array.isArray(details.features) && details.features.length ? (
                <div className="flex flex-wrap gap-2">
                  {details.features.map((feature, i) => (
                    <span
                      key={i}
                      className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${
                        i === 0
                          ? "bg-primary-100 text-primary-700"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500">{DASH}</div>
              )}
            </section>
          </div>
        </div>

        {/* Create Account Action */}
        <div className="mt-10 mb-6 text-center">
          <a
            href="https://diy.sharekhan.com/app/Account/RegisterFOS?grpcd=13057&type=pb&grpid=13492&utm_empcode=PB13057USER&FormType=NSDL&CustomerType=FOS"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-10 py-2 bg-primary-600 text-white text-xl font-bold rounded-xl shadow-lg shadow-primary-500/30 hover:bg-primary-700 hover:shadow-xl hover:shadow-primary-500/40 hover:-translate-y-1 transition-all duration-300"
          >
            Create Account
          </a>
          <p className="text-gray-500 text-sm mt-3">
            Open an account with sharekhan in minutes
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BrokerDetail;
